import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Button } from 'seagull/components/Button';
import { IconButton } from 'seagull/components/IconButton';
import { Box, Column, Row } from 'seagull/components/Layout';
import { useOverlayContext } from 'seagull/components/OverlayContext/OverlayContext';
import { ProgressBar } from 'seagull/components/ProgressBar';
import { Typography } from 'seagull/components/Typography';
import { Send } from 'seagull/icons/emails-messages';
import { CloseCross } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { BulkSendResultsModal } from '../../../BulkSendResultsModal/BulkSendResultsModal';
import { hasRunResults } from '../../../runResults.utils';
import { isTerminal } from '../../../taskModel';
import { useCancelRun } from '../useCancelRun';
import styles from './BulkSendProposalsTaskItem.module.scss';
import { bulkSendSubtitle, bulkSendTitle } from './BulkSendProposalsTaskItem.utils';
const SEND_RUN_CANCEL_BI = {
    keep: 'bulk_send_run_cancel_keep',
    confirm: 'bulk_send_run_cancel_confirm'
};
export const GetBulkSendProposalsTaskItemTID = (parentTestId)=>getTestIds(parentTestId);
export const BulkSendProposalsTaskItem = (param)=>{
    let { task, testId } = param;
    const { t: tasksDrawerTexts } = useLocaleKeys('navigation.tasksDrawer');
    const { BulkSendProposalsTaskItemTestIds } = useMemo(()=>GetBulkSendProposalsTaskItemTID(testId), [
        testId
    ]);
    const { showModal } = useOverlayContext();
    const { jobId, jobName } = task;
    const { api } = useStoreActions();
    const { cancelRun } = useCancelRun({
        runId: jobId,
        texts: tasksDrawerTexts.bulkSendProposals,
        biNames: SEND_RUN_CANCEL_BI,
        cancel: api.draft.cancelBulkSendProposals
    });
    const hasResults = hasRunResults(task);
    const isRunning = !isTerminal(task.status);
    const openDetails = useCallback((event)=>{
        event === null || event === void 0 ? void 0 : event.stopPropagation();
        void showModal({
            biName: 'bulk_send_results',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BulkSendResultsModal, {
                    batchId: jobId,
                    jobName: jobName,
                    onClose: ()=>dismissModal(undefined)
                });
            }
        });
    }, [
        jobId,
        jobName
    ]);
    return /*#__PURE__*/ _jsxs(Row, {
        gap: 16,
        alignItems: "center",
        testId: BulkSendProposalsTaskItemTestIds.Root(),
        className: styles.row,
        onClick: hasResults ? ()=>openDetails() : undefined,
        children: [
            /*#__PURE__*/ _jsx(Box, {
                className: styles.pageType,
                alignItems: "center",
                justifyContent: "center",
                children: /*#__PURE__*/ _jsx(Send, {
                    size: 24
                })
            }),
            /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                className: styles.body,
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        gap: 6,
                        alignItems: "center",
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                weight: "bold",
                                ellipsis: true,
                                testId: BulkSendProposalsTaskItemTestIds.Title(),
                                children: bulkSendTitle(task, tasksDrawerTexts.bulkSendProposals)
                            }),
                            isTerminal(task.status) && task.status !== 'cancelled' && !task.seen && /*#__PURE__*/ _jsx(Box, {
                                className: styles.statusDot,
                                testId: BulkSendProposalsTaskItemTestIds.StatusDot()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        testId: BulkSendProposalsTaskItemTestIds.Subtitle(),
                        children: bulkSendSubtitle(task, tasksDrawerTexts.bulkSendProposals)
                    }),
                    isRunning && /*#__PURE__*/ _jsxs(Row, {
                        gap: 8,
                        alignItems: "center",
                        className: styles.progressRow,
                        testId: BulkSendProposalsTaskItemTestIds.ProgressRow(),
                        children: [
                            /*#__PURE__*/ _jsx(ProgressBar, {
                                className: styles.progressBar,
                                completed: task.progress,
                                total: 100,
                                testId: BulkSendProposalsTaskItemTestIds.ProgressBarSlot()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                skin: "secondary",
                                children: tasksDrawerTexts.common.percent({
                                    value: task.progress
                                })
                            })
                        ]
                    })
                ]
            }),
            isRunning && /*#__PURE__*/ _jsx(IconButton, {
                skin: "transparent",
                size: "small",
                "aria-label": tasksDrawerTexts.bulkSendProposals.cancelRun(),
                bi: {
                    name: 'bulk_send_run_cancel'
                },
                testId: BulkSendProposalsTaskItemTestIds.CancelButton(),
                onClick: ()=>void cancelRun(),
                children: /*#__PURE__*/ _jsx(CloseCross, {
                    size: 20
                })
            }),
            hasResults && /*#__PURE__*/ _jsx(Button, {
                className: styles.detailsButton,
                size: "small",
                bi: {
                    name: 'bulk_send_run_details'
                },
                testId: BulkSendProposalsTaskItemTestIds.DetailsButton(),
                onClick: openDetails,
                children: tasksDrawerTexts.bulkSendProposals.detailsButton()
            })
        ]
    });
};
